/**
 * Pure colour utility functions for runtime tenant branding.
 *
 * All functions are side-effect-free; DOM writes are done separately
 * in the branding redux facade.
 */

/** Parse a 3- or 6-digit hex string (with or without leading #). Returns null if invalid. */
export function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
    const cleaned = hex.startsWith('#') ? hex.slice(1) : hex;

    if (cleaned.length === 3) {
        // Validate that all characters are valid hex digits
        if (!/^[0-9a-fA-F]{3}$/.test(cleaned)) return null;
        const r = parseInt(cleaned[0] + cleaned[0], 16);
        const g = parseInt(cleaned[1] + cleaned[1], 16);
        const b = parseInt(cleaned[2] + cleaned[2], 16);
        if (isNaN(r) || isNaN(g) || isNaN(b)) return null;
        return { r, g, b };
    }

    if (cleaned.length === 6) {
        // Validate that all characters are valid hex digits
        if (!/^[0-9a-fA-F]{6}$/.test(cleaned)) return null;
        const r = parseInt(cleaned.slice(0, 2), 16);
        const g = parseInt(cleaned.slice(2, 4), 16);
        const b = parseInt(cleaned.slice(4, 6), 16);
        if (isNaN(r) || isNaN(g) || isNaN(b)) return null;
        return { r, g, b };
    }

    return null;
}

/** Returns "r,g,b" string suitable for Ionic's --ion-color-*-rgb variables. */
export function hexToRgbString(hex: string): string | null {
    const rgb = hexToRgb(hex);
    if (!rgb) return null;
    return `${rgb.r},${rgb.g},${rgb.b}`;
}

/**
 * WCAG 2.1 relative luminance formula.
 * https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
 */
export function relativeLuminance(r: number, g: number, b: number): number {
    const linearize = (channel: number): number => {
        const c = channel / 255;
        return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    };
    return 0.2126 * linearize(r) + 0.7152 * linearize(g) + 0.0722 * linearize(b);
}

/**
 * Returns '#000000' or '#ffffff' — whichever has ≥ 4.5:1 contrast ratio
 * against the given background hex per WCAG 2.1.
 * Falls back to '#ffffff' if hex is unparseable.
 */
export function contrastColor(hex: string): '#000000' | '#ffffff' {
    const rgb = hexToRgb(hex);
    if (!rgb) return '#ffffff';
    const lum = relativeLuminance(rgb.r, rgb.g, rgb.b);
    // WCAG threshold: (L + 0.05) / (0.05) for white vs (1.05) / (L + 0.05) for black.
    // White contrast = (1 + 0.05) / (lum + 0.05); Black contrast = (lum + 0.05) / (0 + 0.05).
    // Pick black when background is light (lum > 0.179 ≈ midpoint).
    return lum > 0.179 ? '#000000' : '#ffffff';
}

/** Clamp a 0–255 channel value to [0, 255]. */
const clamp = (value: number): number => Math.max(0, Math.min(255, Math.round(value)));

/** Convert an {r,g,b} object back to a lowercase 6-digit hex string. */
function rgbToHex(r: number, g: number, b: number): string {
    return '#' + [r, g, b].map(c => clamp(c).toString(16).padStart(2, '0')).join('');
}

/**
 * Darken a hex colour by `amount` (0–1). Amount 0 = no change, 1 = black.
 * Returns the original hex if unparseable.
 */
export function darken(hex: string, amount: number): string {
    const rgb = hexToRgb(hex);
    if (!rgb) return hex;
    const factor = 1 - Math.max(0, Math.min(1, amount));
    return rgbToHex(rgb.r * factor, rgb.g * factor, rgb.b * factor);
}

/**
 * Lighten a hex colour by `amount` (0–1). Amount 0 = no change, 1 = white.
 * Returns the original hex if unparseable.
 */
export function lighten(hex: string, amount: number): string {
    const rgb = hexToRgb(hex);
    if (!rgb) return hex;
    const factor = Math.max(0, Math.min(1, amount));
    return rgbToHex(
        rgb.r + (255 - rgb.r) * factor,
        rgb.g + (255 - rgb.g) * factor,
        rgb.b + (255 - rgb.b) * factor,
    );
}

// --- Runtime brand-token injection + per-host cache -------------------------
// These are intentionally free of any redux/store import so they can run at
// bootstrap (index.tsx) before React mounts, and be reused by the branding
// facade — without reintroducing a circular dependency.

/** Every CSS custom property we write, so unmount can cleanly remove them. */
export const BRAND_CSS_VAR_NAMES = [
    '--brand-primary', '--brand-secondary', '--brand-on-primary', '--brand-on-secondary',
    '--ion-color-primary', '--ion-color-primary-rgb', '--ion-color-primary-contrast',
    '--ion-color-primary-contrast-rgb', '--ion-color-primary-shade', '--ion-color-primary-tint',
    '--ion-color-secondary', '--ion-color-secondary-rgb', '--ion-color-secondary-contrast',
    '--ion-color-secondary-contrast-rgb', '--ion-color-secondary-shade', '--ion-color-secondary-tint',
] as const;

/** Write the brand tokens (and the Ionic primary/secondary mapping) to :root. */
export function applyBrandTokens(primary: string, secondary: string): void {
    const el = document.documentElement;
    const set = (name: string, value: string | null) => el.style.setProperty(name, value ?? '');
    const onPrimary = contrastColor(primary);
    const onSecondary = contrastColor(secondary);

    set('--brand-primary', primary);
    set('--brand-secondary', secondary);
    set('--brand-on-primary', onPrimary);
    set('--brand-on-secondary', onSecondary);

    set('--ion-color-primary', primary);
    set('--ion-color-primary-rgb', hexToRgbString(primary));
    set('--ion-color-primary-contrast', onPrimary);
    set('--ion-color-primary-contrast-rgb', hexToRgbString(onPrimary));
    set('--ion-color-primary-shade', darken(primary, 0.12));
    set('--ion-color-primary-tint', lighten(primary, 0.1));

    set('--ion-color-secondary', secondary);
    set('--ion-color-secondary-rgb', hexToRgbString(secondary));
    set('--ion-color-secondary-contrast', onSecondary);
    set('--ion-color-secondary-contrast-rgb', hexToRgbString(onSecondary));
    set('--ion-color-secondary-shade', darken(secondary, 0.12));
    set('--ion-color-secondary-tint', lighten(secondary, 0.1));
}

/** Remove all brand tokens, reverting to the app's default variables.css theme. */
export function removeBrandTokens(): void {
    const el = document.documentElement;
    BRAND_CSS_VAR_NAMES.forEach(name => el.style.removeProperty(name));
}

export interface CachedBranding {
    primary: string;
    secondary: string;
    name: string | null;
    logoUrl: string | null;
    currencyPrefix: string;
}

const CACHE_PREFIX = 'sw_branding:';
const cacheKey = (host: string) => `${CACHE_PREFIX}${host}`;
const currentHost = (): string => (typeof window !== 'undefined' ? window.location.host : '');
const currentPath = (): string => (typeof window !== 'undefined' ? window.location.pathname : '');

/**
 * True if `path` is a public storefront (`/shop`) route. Tenant branding is
 * scoped to these routes ONLY — it must never bleed into the authenticated
 * internal app (which shares this single build and, on a shop host, the same
 * origin). The exact/`/shop/` test deliberately excludes the authenticated shop
 * (`/my-shop`) and lookalikes (`/shopping`).
 */
export function isStorefrontRoute(path: string = currentPath()): boolean {
    return path === '/shop' || path.startsWith('/shop/');
}

/** Read cached branding for a host (defaults to the current host). */
export function readCachedBranding(host: string = currentHost()): CachedBranding | null {
    try {
        const raw = window.localStorage.getItem(cacheKey(host));
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed.primary === 'string' && typeof parsed.secondary === 'string') {
            return parsed as CachedBranding;
        }
        return null;
    } catch {
        return null; // private mode / unavailable storage
    }
}

/** True if we have cached branding for the current host (used to skip the neutral gate). */
export function hasCachedBranding(host: string = currentHost()): boolean {
    return readCachedBranding(host) !== null;
}

/** Persist branding for the current host (stale-while-revalidate source). */
export function writeCachedBranding(data: CachedBranding, host: string = currentHost()): void {
    try {
        window.localStorage.setItem(cacheKey(host), JSON.stringify(data));
    } catch {
        /* ignore */
    }
}

/**
 * Bootstrap hook: synchronously apply cached brand tokens for the current host
 * (before React mounts) so returning storefront visitors never see the
 * default-theme flash. Returns true if cached tokens were applied.
 *
 * Storefront-only gate: applies ONLY on a `/shop` route. Because the storefront
 * and the authenticated app share one build on one host, an ungated bootstrap
 * would repaint tenant colours onto `:root` on a refresh/direct-load of an
 * authenticated route (the runtime hook only tears tokens down on storefront
 * unmount, and never mounts on authed routes). Gating here keeps tenant branding
 * off the internal app until real white-labelling ships.
 */
export function applyCachedBrandTokens(): boolean {
    if (!isStorefrontRoute()) return false;
    const cached = readCachedBranding();
    if (!cached) return false;
    applyBrandTokens(cached.primary, cached.secondary);
    return true;
}
